// Story 8.10 (ADO-2019): picking up handheld terminal (HHT) orders.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the five HHT
// routes on `order-capture` (story DV-12, the HTTP contract; DV-32, each waiting order is
// reviewed and added on its own; DV-39, the count apart from the details):
//   GET  /api/v1/hht-orders/waiting-count         how many wait, one database count (DV-39)
//   GET  /api/v1/hht-orders/default-value-centre-cart
//                                                  the send guard's Default VC cart (DV-73)
//   GET  /api/v1/hht-orders                       what is waiting, and the history (AC1, AC2, DV-33)
//   GET  /api/v1/hht-orders/{hhtOrderId}/review   what one waiting order holds (AC3, AC4, DV-32),
//                                                  or who accepted it and when (Q4)
//   POST /api/v1/hht-orders/acceptance            { hhtOrderIds, keptSubstitutes } (AC3, AC4, DV-18)
// The wire shapes are a LOCK-STEP COPY of the DTOs the order-capture module exports;
// keep the two in sync. camelCase JSON, no envelope, RFC 7807 errors, ISO 8601 UTC.
//
// Every body is validated: a 2xx in the wrong shape is a contract violation, surfaced
// as an error rather than painted as `undefined` on the page.

import { problemDetail } from './basket.client';
import { API_PREFIX, apiFetch, PROBLEM_TYPE_HEADER } from './shoplink-api';

export const HHT_ORDERS_PATH = `${API_PREFIX}/hht-orders`;

/** `GET /api/v1/hht-orders/waiting-count` (story DV-39): the header badge and the /choose
 *  card's title and button. When `applies` is false `waitingCount` is 0. */
export interface HhtWaitingCount {
  readonly applies: boolean;
  readonly waitingCount: number;
}

/** Phase 4 (story DV-73): the Default VC cart the send guard looks up, by its depot and the
 *  account it is keyed by (a Value Centre's `DEPOT` cart). */
export interface HhtValueCentreCart {
  readonly depotId: string;
  readonly customerAccountCode: string;
}

/** One cart a waiting order adds to (story DV-36): its raw depot code and, for the Chill Fruit &
 *  Veg cart only, its entry kind. Chill and Fruit & Veg at depot 25 are two of these. */
export interface HhtWaitingCart {
  readonly depotCode: string;
  readonly entryKind?: HhtCartEntryKind;
}

/** A held order still waiting to be added (oldest first on the wire). The server also sends
 *  `depotCodes`, the raw codes of `carts`, for bundles older than `carts`; this copy leaves it
 *  out and does not require it, so the server can drop it once those bundles are gone. */
export interface HhtWaitingOrder {
  readonly hhtOrderId: string;
  readonly receivedAt: string;
  /** The Ready lines and cases, as the review's "Ready to add" counts them (story DV-39). */
  readonly lineCount: number;
  readonly caseCount: number;
  /** Discontinued and Unavailable lines (story DV-33; unknown codes are never counted, DV-50). */
  readonly attentionLineCount: number;
  /** The carts something will be added to, each once, in display order (story DV-30): 21, 25,
   *  85, and at 25 Chill's cart before Fruit & Veg. */
  readonly carts: readonly HhtWaitingCart[];
}

/** How many accepted orders the history lists at most (the server's `HHT_HISTORY_LIMIT`). */
export const HHT_HISTORY_LIMIT = 20;

/** An accepted order, for the HHT history (newest first, at most {@link HHT_HISTORY_LIMIT}). */
export interface HhtHistoryOrder {
  readonly hhtOrderId: string;
  readonly receivedAt: string;
  readonly acceptedAt: string;
  /** The lines and cases the accept added, as the after-accept screen showed them. */
  readonly lineCount: number;
  readonly caseCount: number;
  /** The scanned lines that reached no cart ("N attention", or "Clean" at 0). */
  readonly attentionLineCount: number;
  /** Who accepted it (Q10); null when the name could not be read. */
  readonly acceptedByName: string | null;
}

/** `applies` is decided server-side (story D-8); when false both lists are empty. */
export interface HhtOrdersOverview {
  readonly applies: boolean;
  readonly waiting: readonly HhtWaitingOrder[];
  readonly history: readonly HhtHistoryOrder[];
  /** How many accepted orders the Customer has in all (the history pill). */
  readonly historyTotal: number;
}

/**
 * Enabler E8-G (ADO-3109), task 5.1: the entry kind of a cart that is not its depot's own. Chill
 * Fruit & Veg is a cart of its own at depot 25, so its rows carry `entryKind: 'FRUIT_AND_VEG'`
 * beside the depot id; a depot's own cart carries none (the cart routes' `entryKind`).
 */
export type HhtCartEntryKind = 'FRUIT_AND_VEG';

/** A product and its cases, as a substitute and a not-added line carry it. */
export interface HhtReviewLine {
  readonly skuCode: string;
  readonly productName: string | null;
  readonly quantity: number;
}

/** One depot cart the ready lines route to (story DV-1, DV-3), with its counts only: the
 *  depot rows do not expand to list their products (OQ-21). */
export interface HhtReviewDepot {
  readonly depotId: string;
  /** Present only for the Chill Fruit & Veg cart. */
  readonly entryKind?: HhtCartEntryKind;
  readonly depotCode: string;
  readonly depotName: string;
  readonly lineCount: number;
  readonly caseCount: number;
}

export interface HhtSubstitute {
  readonly skuCode: string;
  readonly productName: string | null;
  readonly quantity: number;
  readonly depotId: string;
  /** Present only when the substitute goes to the Chill Fruit & Veg cart. */
  readonly entryKind?: HhtCartEntryKind;
  readonly depotName: string;
}

/** DV-22. `DISCONTINUED`: the product's row is no longer live, or its listing at its routed
 *  depot is inactive (DV-3). `UNAVAILABLE`: live, but
 *  none of the Customer's depots carries it or the add-to-cart gate blocks it. Either carries
 *  a substitute when one qualifies. Unknown codes are never sent (DV-50). */
export type HhtAttentionStatus = 'DISCONTINUED' | 'UNAVAILABLE';

export interface HhtAttentionLine {
  readonly skuCode: string;
  readonly productName: string | null;
  readonly quantity: number;
  readonly depotName: string | null;
  readonly status: HhtAttentionStatus;
  readonly substitute: HhtSubstitute | null;
}

/**
 * Story 8.3 (ADO-2012), D-23: one depot cart line as the review saw it. The accept sends these
 * back (`seenCartLines`), so a line that changed since is not added and nothing is doubled.
 * The token is opaque.
 */
export interface HhtReviewCartLine {
  readonly depotId: string;
  /** Present only for a line of the Chill Fruit & Veg cart. */
  readonly entryKind?: HhtCartEntryKind;
  readonly skuCode: string;
  readonly token: string;
}

/**
 * One waiting order's review (story DV-32): rows of the file that scan the same product are
 * one line. `hhtOrderId` is what an accept names.
 */
export interface HhtOrderReview {
  readonly status: 'WAITING';
  readonly hhtOrderId: string;
  /** When the file was received (it carries no scan time). */
  readonly receivedAt: string;
  readonly ready: {
    readonly lineCount: number;
    readonly caseCount: number;
    readonly depots: readonly HhtReviewDepot[];
  };
  readonly attention: readonly HhtAttentionLine[];
  /** Story 8.3 (ADO-2012), D-23: the depot cart lines the review was read against. Absent on
   *  an older server, whose accept adds on top with no check. */
  readonly cartLines?: readonly HhtReviewCartLine[];
}

/** An order already accepted, when its review is asked for (Q4, "Already added"). */
export interface HhtOrderAlreadyAccepted {
  readonly status: 'ACCEPTED';
  readonly hhtOrderId: string;
  /** ISO 8601 UTC; formatted in the viewer's zone (DV-38). */
  readonly acceptedAt: string;
  readonly acceptedByName: string | null;
}

/** What `getHhtOrderReview` answers, told apart by `status`. */
export type HhtOrderReviewResponse = HhtOrderReview | HhtOrderAlreadyAccepted;

export interface HhtCartAdded {
  readonly depotId: string;
  /** Present only for the Chill Fruit & Veg cart. */
  readonly entryKind?: HhtCartEntryKind;
  /** The account the cart is keyed on (story DV-68, phase 4): "Go to cart" opens it. */
  readonly customerAccountCode: string;
  readonly depotCode: string;
  readonly depotName: string;
  readonly lineCount: number;
  readonly caseCount: number;
}

export interface HhtLineNotAdded {
  readonly skuCode: string;
  readonly productName: string | null;
  readonly quantity: number;
  /** The depot it would have gone to, or null when it had none. */
  readonly depotName: string | null;
  readonly reason: string;
  /** Story 8.3 (ADO-2012), D-23: with `CHANGED_IN_CART`, the cart line's quantity now (card
   *  08's "it's now 5"), distinct from this line's own `quantity`. */
  readonly currentQuantity?: number;
}

/**
 * The most cases one cart line can hold. A LOCK-STEP COPY of order-capture's
 * `MAX_LINE_QUANTITY` (`domain/cart-line.ts`): the web does not import backend constants. Q3's
 * "Cart limit reached" copy names it.
 */
export const CART_LINE_MAX_CASES = 100_000;

/** One attention line whose shown substitute the Shopper kept (story DV-4, DV-24). */
export interface HhtKeptSubstitute {
  readonly skuCode: string;
  readonly substituteSkuCode: string;
}

export interface HhtAcceptanceResult {
  readonly added: { readonly lineCount: number; readonly caseCount: number };
  readonly carts: readonly HhtCartAdded[];
  readonly notAdded: readonly HhtLineNotAdded[];
}

/**
 * The `x-shoplink-problem-type` slug on the 409 an accept gets when a named order is no
 * longer waiting (story D-12, DV-18): another accept won and nothing was written.
 */
export const HHT_ORDER_NOT_WAITING_PROBLEM_TYPE = 'hht-order-not-waiting';

/**
 * A 404 or 409 from the review or the acceptance. `message` is the server's Problem
 * Details `detail` (written for the Shopper) when it sent one, else a fixed line for the
 * status, so it is always safe to render. `status` lets the page decide to refresh;
 * `problemType` is the response's `x-shoplink-problem-type` slug, when it carries one.
 */
export class HhtOrderProblemError extends Error {
  constructor(
    message: string,
    readonly status: 404 | 409,
    readonly problemType: string | undefined = undefined,
  ) {
    super(message);
    this.name = 'HhtOrderProblemError';
  }
}

const FALLBACK_PROBLEM_MESSAGE: Record<404 | 409, string> = {
  404: 'This handheld order is no longer available.',
  409: 'This handheld order could not be added. It may already be in your carts.',
};

type JsonRecord = Record<string, unknown>;

function record(value: unknown): JsonRecord | null {
  return typeof value === 'object' && value !== null ? (value as JsonRecord) : null;
}

const isString = (value: unknown): value is string => typeof value === 'string';
const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';
const isInstant = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

function isOrderCounts(r: JsonRecord): boolean {
  return (
    isString(r['hhtOrderId']) &&
    isInstant(r['receivedAt']) &&
    isCount(r['lineCount']) &&
    isCount(r['caseCount']) &&
    isCount(r['attentionLineCount'])
  );
}

function isWaitingCart(value: unknown): value is HhtWaitingCart {
  const r = record(value);
  return r !== null && isString(r['depotCode']) && isCartEntryKind(r['entryKind']);
}

function isWaitingOrder(value: unknown): value is HhtWaitingOrder {
  const r = record(value);
  const carts = r?.['carts'];
  return r !== null && isOrderCounts(r) && Array.isArray(carts) && carts.every(isWaitingCart);
}

function isHistoryOrder(value: unknown): value is HhtHistoryOrder {
  const r = record(value);
  return (
    r !== null &&
    isOrderCounts(r) &&
    isInstant(r['acceptedAt']) &&
    isNullableString(r['acceptedByName'])
  );
}

function parseWaitingCount(body: unknown): HhtWaitingCount {
  const r = record(body);
  const applies = r?.['applies'];
  const waitingCount = r?.['waitingCount'];
  if (typeof applies !== 'boolean' || !isCount(waitingCount)) {
    throw new Error('HHT waiting count response was not in the expected shape');
  }
  return { applies, waitingCount };
}

function parseDefaultValueCentreCart(body: unknown): HhtValueCentreCart | null {
  const r = record(body);
  if (r === null || !('defaultValueCentreCart' in r)) {
    throw new Error('HHT Default VC cart response was not in the expected shape');
  }
  const cart = r['defaultValueCentreCart'];
  if (cart === null) return null;
  const c = record(cart);
  const depotId = c?.['depotId'];
  const customerAccountCode = c?.['customerAccountCode'];
  if (
    typeof depotId !== 'string' ||
    depotId.trim() === '' ||
    typeof customerAccountCode !== 'string' ||
    customerAccountCode.trim() === ''
  ) {
    throw new Error('HHT Default VC cart response was not in the expected shape');
  }
  return { depotId, customerAccountCode };
}

function parseOverview(body: unknown): HhtOrdersOverview {
  const r = record(body);
  const applies = r?.['applies'];
  const waiting = r?.['waiting'];
  const history = r?.['history'];
  const historyTotal = r?.['historyTotal'];
  if (
    typeof applies !== 'boolean' ||
    !Array.isArray(waiting) ||
    !waiting.every(isWaitingOrder) ||
    !Array.isArray(history) ||
    !history.every(isHistoryOrder) ||
    !isCount(historyTotal)
  ) {
    throw new Error('HHT orders response was not in the expected shape');
  }
  return { applies, waiting, history, historyTotal };
}

function isReviewLine(value: unknown): value is HhtReviewLine {
  const r = record(value);
  return (
    r !== null &&
    isString(r['skuCode']) &&
    isNullableString(r['productName']) &&
    isCount(r['quantity'])
  );
}

/** An absent entry kind (a depot's own cart) or the one cart kind that names itself. */
function isCartEntryKind(value: unknown): boolean {
  return value === undefined || value === 'FRUIT_AND_VEG';
}

function isReviewDepot(value: unknown): value is HhtReviewDepot {
  const r = record(value);
  return (
    r !== null &&
    isString(r['depotId']) &&
    isCartEntryKind(r['entryKind']) &&
    isString(r['depotCode']) &&
    isString(r['depotName']) &&
    isCount(r['lineCount']) &&
    isCount(r['caseCount'])
  );
}

/** Only the fields the page reads, so anything else on the wire is not carried along. */
function depotCounts(depot: HhtReviewDepot): HhtReviewDepot {
  const { depotId, entryKind, depotCode, depotName, lineCount, caseCount } = depot;
  return {
    depotId,
    ...(entryKind === undefined ? {} : { entryKind }),
    depotCode,
    depotName,
    lineCount,
    caseCount,
  };
}

function isSubstitute(value: unknown): value is HhtSubstitute {
  const r = record(value);
  return (
    r !== null &&
    isReviewLine(value) &&
    isString(r['depotId']) &&
    isCartEntryKind(r['entryKind']) &&
    isString(r['depotName'])
  );
}

function isAttentionLine(value: unknown): value is HhtAttentionLine {
  const r = record(value);
  const status = r?.['status'];
  const substitute = r?.['substitute'];
  return (
    r !== null &&
    isReviewLine(value) &&
    isNullableString(r['depotName']) &&
    (status === 'DISCONTINUED' || status === 'UNAVAILABLE') &&
    (substitute === null || isSubstitute(substitute))
  );
}

function parseAlreadyAccepted(r: JsonRecord): HhtOrderAlreadyAccepted {
  const hhtOrderId = r['hhtOrderId'];
  const acceptedAt = r['acceptedAt'];
  const acceptedByName = r['acceptedByName'];
  if (!isString(hhtOrderId) || !isInstant(acceptedAt) || !isNullableString(acceptedByName)) {
    throw new Error('HHT order review response was not in the expected shape');
  }
  return { status: 'ACCEPTED', hhtOrderId, acceptedAt, acceptedByName };
}

function isReviewCartLine(value: unknown): value is HhtReviewCartLine {
  const r = record(value);
  return (
    r !== null &&
    isString(r['depotId']) &&
    isCartEntryKind(r['entryKind']) &&
    isString(r['skuCode']) &&
    isString(r['token'])
  );
}

function parseReview(body: unknown): HhtOrderReviewResponse {
  const r = record(body);
  if (r !== null && r['status'] === 'ACCEPTED') return parseAlreadyAccepted(r);
  const ready = record(r?.['ready']);
  const depots = ready?.['depots'];
  const attention = r?.['attention'];
  const hhtOrderId = r?.['hhtOrderId'];
  const receivedAt = r?.['receivedAt'];
  if (
    r === null ||
    r['status'] !== 'WAITING' ||
    ready === null ||
    !isString(hhtOrderId) ||
    !isInstant(receivedAt) ||
    !isCount(ready['lineCount']) ||
    !isCount(ready['caseCount']) ||
    !Array.isArray(depots) ||
    !depots.every(isReviewDepot) ||
    !Array.isArray(attention) ||
    !attention.every(isAttentionLine) ||
    (r['cartLines'] !== undefined &&
      (!Array.isArray(r['cartLines']) || !r['cartLines'].every(isReviewCartLine)))
  ) {
    throw new Error('HHT order review response was not in the expected shape');
  }
  const cartLines = r['cartLines'] as readonly HhtReviewCartLine[] | undefined;
  return {
    status: 'WAITING',
    hhtOrderId,
    receivedAt,
    ready: {
      lineCount: ready['lineCount'],
      caseCount: ready['caseCount'],
      depots: depots.map(depotCounts),
    },
    attention,
    ...(cartLines === undefined ? {} : { cartLines }),
  };
}

function isCartAdded(value: unknown): value is HhtCartAdded {
  const r = record(value);
  return (
    r !== null &&
    isString(r['depotId']) &&
    isCartEntryKind(r['entryKind']) &&
    isString(r['customerAccountCode']) &&
    isString(r['depotCode']) &&
    isString(r['depotName']) &&
    isCount(r['lineCount']) &&
    isCount(r['caseCount'])
  );
}

function isLineNotAdded(value: unknown): value is HhtLineNotAdded {
  const r = record(value);
  return (
    r !== null &&
    isReviewLine(value) &&
    isNullableString(r['depotName']) &&
    isString(r['reason']) &&
    (r['currentQuantity'] === undefined || isCount(r['currentQuantity']))
  );
}

function parseAcceptance(body: unknown): HhtAcceptanceResult {
  const r = record(body);
  const added = record(r?.['added']);
  const carts = r?.['carts'];
  const notAdded = r?.['notAdded'];
  if (
    r === null ||
    added === null ||
    !isCount(added['lineCount']) ||
    !isCount(added['caseCount']) ||
    !Array.isArray(carts) ||
    !carts.every(isCartAdded) ||
    !Array.isArray(notAdded) ||
    !notAdded.every(isLineNotAdded)
  ) {
    throw new Error('HHT order acceptance response was not in the expected shape');
  }
  return {
    added: { lineCount: added['lineCount'], caseCount: added['caseCount'] },
    carts,
    notAdded,
  };
}

/** A 404 / 409 becomes an `HhtOrderProblemError`; any other failure a plain status-coded
 *  Error the page replaces with a generic line. */
async function failure(res: Response, fallback: string): Promise<Error> {
  if (res.status === 404 || res.status === 409) {
    const detail = await problemDetail(res);
    return new HhtOrderProblemError(
      detail ?? FALLBACK_PROBLEM_MESSAGE[res.status],
      res.status,
      res.headers.get(PROBLEM_TYPE_HEADER) ?? undefined,
    );
  }
  return new Error(`${fallback} (${res.status})`);
}

function signalInit(signal: AbortSignal | undefined): RequestInit {
  return signal === undefined ? {} : { signal };
}

/** Read how many handheld orders wait (story DV-39): the header badge and the /choose card. */
export async function getHhtWaitingCount(
  options: { readonly signal?: AbortSignal } = {},
): Promise<HhtWaitingCount> {
  const res = await apiFetch(`${HHT_ORDERS_PATH}/waiting-count`, signalInit(options.signal));
  if (!res.ok) throw new Error(`Failed to load the HHT waiting count (${res.status})`);
  return parseWaitingCount(await res.json());
}

/** Phase 4 (story DV-73): the caller's Default VC cart, or null. Read by the send guard only on
 *  a manual send of a cart outside Ambient, Chill and Alcohol while HHT orders wait. */
export async function getHhtDefaultValueCentreCart(): Promise<HhtValueCentreCart | null> {
  const res = await apiFetch(`${HHT_ORDERS_PATH}/default-value-centre-cart`);
  if (!res.ok) throw new Error(`Failed to load the HHT Default VC cart (${res.status})`);
  return parseDefaultValueCentreCart(await res.json());
}

/** Read whether the HHT workflow applies to the caller, what is waiting (classified, with
 *  the Ready counts) and the history. */
export async function getHhtOrders(
  options: { readonly signal?: AbortSignal } = {},
): Promise<HhtOrdersOverview> {
  const res = await apiFetch(HHT_ORDERS_PATH, signalInit(options.signal));
  if (!res.ok) throw new Error(`Failed to load HHT orders (${res.status})`);
  return parseOverview(await res.json());
}

/** Read one order: a waiting order's review, the ready lines per depot and the lines that
 *  need attention (AC3 disclosure, AC4, DV-32), or for an accepted one who accepted it and
 *  when (Q4). An unknown id, or another Customer's, is the 404 problem. */
export async function getHhtOrderReview(
  hhtOrderId: string,
  options: { readonly signal?: AbortSignal } = {},
): Promise<HhtOrderReviewResponse> {
  const res = await apiFetch(
    `${HHT_ORDERS_PATH}/${encodeURIComponent(hhtOrderId)}/review`,
    signalInit(options.signal),
  );
  if (!res.ok) throw await failure(res, 'Failed to load the handheld order');
  return parseReview(await res.json());
}

/**
 * The substitutes the review showed, minus the ones the Shopper removed (story DV-4,
 * DV-49): what an accept names as kept.
 */
export function keptSubstitutesFrom(
  review: HhtOrderReview,
  removedSubstitutes: ReadonlySet<string>,
): HhtKeptSubstitute[] {
  return review.attention.flatMap((line) =>
    line.substitute !== null && !removedSubstitutes.has(line.skuCode)
      ? [{ skuCode: line.skuCode, substituteSkuCode: line.substitute.skuCode }]
      : [],
  );
}

/**
 * Accept the named waiting orders into the depot carts (story DV-18; the page names the one
 * order it reviewed, DV-32). If any named order is no longer waiting the accept is refused
 * (the typed 409, D-12) with nothing written. `keptSubstitutes` names the attention lines
 * whose shown substitute the Shopper kept (story DV-4); a line not named adds no substitute.
 * The server re-classifies and re-routes every line and validates each kept substitute; one
 * that no longer qualifies is not added (`SUBSTITUTE_NO_LONGER_AVAILABLE`, story DV-24/DV-51). The named orders are accepted even when nothing can be added (story DV-23).
 */
export async function acceptHhtOrders(
  hhtOrderIds: readonly string[],
  keptSubstitutes: readonly HhtKeptSubstitute[],
  /** Story 8.3 (ADO-2012), D-23: the review's `cartLines`, sent back; omitted when the review
   *  carried none (an older server). */
  seenCartLines?: readonly HhtReviewCartLine[],
): Promise<HhtAcceptanceResult> {
  const res = await apiFetch(`${HHT_ORDERS_PATH}/acceptance`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      hhtOrderIds,
      keptSubstitutes,
      ...(seenCartLines === undefined ? {} : { seenCartLines }),
    }),
  });
  if (!res.ok) throw await failure(res, 'Failed to add the handheld orders to your carts');
  return parseAcceptance(await res.json());
}
